"use client";

import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "../../lib/utils";
import { Input, type InputProps } from "./input";
import { FormField, type FormFieldProps } from "./form-field";

export type MaskedInputProps = Omit<InputProps, "type"> & {
	initiallyVisible?: boolean;
};

const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
	({ initiallyVisible = false, className, ...props }, ref) => {
		const [isVisible, setIsVisible] = React.useState(initiallyVisible);
		return (
			<div className="relative w-full">
				<Input
					ref={ref}
					type={isVisible ? "text" : "password"}
					className={cn("pr-9", className)}
					{...props}
				/>
				<button
					type="button"
					aria-label={isVisible ? "Hide" : "Show"}
					onClick={() => setIsVisible((v) => !v)}
					className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600"
				>
					{isVisible ? (
						<EyeOff className="size-4" />
					) : (
						<Eye className="size-4" />
					)}
				</button>
			</div>
		);
	},
);
MaskedInput.displayName = "MaskedInput";

export type MaskedInputFieldProps = MaskedInputProps &
	Pick<
		FormFieldProps,
		| "label"
		| "labelFor"
		| "description"
		| "hint"
		| "validationMessage"
		| "isRequired"
		| "isInvalid"
	>;

const MaskedInputField = React.forwardRef<
	HTMLInputElement,
	MaskedInputFieldProps
>(
	(
		{
			label,
			labelFor,
			description,
			hint,
			validationMessage,
			isRequired,
			isInvalid,
			...props
		},
		ref,
	) => (
		<FormField
			label={label}
			labelFor={labelFor}
			description={description}
			hint={hint}
			validationMessage={validationMessage}
			isRequired={isRequired}
			isInvalid={isInvalid}
		>
			<MaskedInput ref={ref} isInvalid={isInvalid} {...props} />
		</FormField>
	),
);
MaskedInputField.displayName = "MaskedInputField";

export { MaskedInput, MaskedInputField };
